import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

type PortalSlotContextType = {
    containers: Record<string, HTMLElement | null>;
    registerContainer: (name: string, element: HTMLElement | null) => void;
};

const PortalSlotContext = createContext<PortalSlotContextType | undefined>(undefined);

const DEFAULT_SLOT = 'default';

/** Wrap the subtree that contains both the slot target and any components rendering into it. */
export const PortalSlotProvider = (props: { children: ReactNode }) => {
    const [containers, setContainers] = useState<Record<string, HTMLElement | null>>({});

    const registerContainer = useCallback((name: string, element: HTMLElement | null) => {
        setContainers((prev) => (prev[name] === element ? prev : { ...prev, [name]: element }));
    }, []);

    return <PortalSlotContext.Provider value={{ containers, registerContainer }}>{props.children}</PortalSlotContext.Provider>;
};

/** Marks the location in the tree where portalled content is rendered. */
export const PortalSlotTarget = (props: { name?: string; className?: string; style?: React.CSSProperties }) => {
    const { name = DEFAULT_SLOT, className, style } = props;
    const context = useContext(PortalSlotContext);
    const registerContainer = context?.registerContainer;

    const setRef = useCallback(
        (element: HTMLDivElement | null) => {
            registerContainer?.(name, element);
        },
        [registerContainer, name]
    );

    return <div ref={setRef} className={className} style={style} />;
};

/** Renders its children into the matching PortalSlotTarget. */
export const PortalSlotContent = (props: { name?: string; children: ReactNode }) => {
    const { name = DEFAULT_SLOT, children } = props;
    const context = useContext(PortalSlotContext);
    const container = context?.containers[name];

    if (!container) return null;
    return createPortal(children, container);
};
